import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ShieldCheck, ChevronDown, ChevronRight } from "lucide-react";
import { products, productList } from "@/data/products";
import PlaceholderImage from "@/components/PlaceholderImage";
import SectionReveal from "@/components/SectionReveal";
import ProductCard from "@/components/ProductCard";
import { getGallery, getIngredientGraphic, BENEFIT_BANNERS } from "@/data/assets";

/* -------- Gallery thumb (real image) -------- */
function GalleryThumb({ index, active, onClick, label, src }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`pdp-gallery-thumb-${index}`}
      className={`relative aspect-square overflow-hidden border transition-colors ${
        active ? "border-black" : "border-black/10 hover:border-black/40"
      } bg-white`}
      aria-label={`Show image ${index + 1}`}
      data-placeholder-label={label}
    >
      {src ? (
        <img
          src={src}
          alt={label}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-heading uppercase tracking-[0.14em] text-[10px] font-semibold text-black/60">
            {`Image 0${index + 1}`}
          </span>
        </div>
      )}
    </button>
  );
}

/* -------- FAQ Accordion row -------- */
function AccordionRow({ q, a, id }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-black/10">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`pdp-faq-toggle-${id}`}
        aria-expanded={open}
        className="w-full flex items-center justify-between text-left py-5 group"
      >
        <span className="font-heading uppercase tracking-[0.12em] text-sm text-black font-semibold group-hover:text-[color:var(--mp-green-hover)] transition-colors">
          {q}
        </span>
        <ChevronDown
          size={18}
          className={`text-black/50 transition-transform ${open ? "rotate-180 text-black" : ""}`}
        />
      </button>
      <div className={`overflow-hidden transition-[max-height] duration-300 ${open ? "max-h-96" : "max-h-0"}`}>
        <p className="pb-5 pr-8 font-body text-black/70 leading-relaxed">{a}</p>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const product = products[slug];
  const [flavour, setFlavour] = useState(product?.flavours[0]?.id);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (product) {
      setFlavour(product.flavours[0]?.id);
      setGalleryIndex(0);
      setQuantity(1);
    }
  }, [slug, product]);

  // reset gallery index when flavour changes
  useEffect(() => {
    setGalleryIndex(0);
  }, [flavour]);

  if (!product) {
    return (
      <section className="min-h-[60vh] flex items-center justify-center py-24 bg-white">
        <div className="text-center">
          <div className="mp-eyebrow">404</div>
          <h1 className="mt-3 font-display text-5xl uppercase">Product Not Found</h1>
          <Link to="/products" className="mp-btn mp-btn-primary mt-8 inline-flex">
            Back to Products
          </Link>
        </div>
      </section>
    );
  }

  const activeFlavour = product.flavours.find((f) => f.id === flavour) || product.flavours[0];
  const related = productList.filter((p) => p.slug !== product.slug);
  const productHeroLabel = `Product Hero – ${product.name}`;
  const gallery = getGallery(product.slug, activeFlavour.id);
  const mainSrc = gallery[galleryIndex] || gallery[0];
  const ingredientSrc = getIngredientGraphic(product.slug, activeFlavour.id);

  return (
    <>
      {/* ============ PDP TOP ============ */}
      <section data-testid="pdp-hero" className="bg-white">
        <div className="mp-container pt-6 pb-4">
          <nav
            aria-label="breadcrumb"
            className="font-heading uppercase tracking-[0.18em] text-[11px] font-semibold text-black/50"
            data-testid="pdp-breadcrumb"
          >
            <Link to="/" className="hover:text-black">Home</Link>
            <span className="mx-2"><ChevronRight size={11} className="inline-block" /></span>
            <Link to="/products" className="hover:text-black">Products</Link>
            <span className="mx-2"><ChevronRight size={11} className="inline-block" /></span>
            <span className="text-black">{product.name}</span>
          </nav>
        </div>

        <div className="mp-container pb-16 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <SectionReveal>
              <PlaceholderImage
                label={`${productHeroLabel} · ${activeFlavour.name}`}
                ratio="square"
                testId="pdp-gallery-main"
                src={mainSrc}
                alt={`${product.name} — ${activeFlavour.name}`}
              />
            </SectionReveal>

            <div className="mt-4 grid grid-cols-4 gap-3">
              {gallery.slice(0, 4).map((g, i) => (
                <GalleryThumb
                  key={g + i}
                  index={i}
                  active={i === galleryIndex}
                  onClick={() => setGalleryIndex(i)}
                  label={`Product Gallery Image 0${i + 1}`}
                  src={g}
                />
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <SectionReveal>
              <div className="mp-eyebrow-green" data-testid="pdp-vendor">MusclePharm</div>
            </SectionReveal>
            <SectionReveal delay={80}>
              <h1
                data-testid="pdp-title"
                className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] text-black"
              >
                {product.name}
              </h1>
            </SectionReveal>
            <SectionReveal delay={140}>
              <div className="mt-4 flex items-end gap-3 flex-wrap" data-testid="pdp-price-block">
                <div className="font-display text-4xl text-black leading-none" data-testid="pdp-price">
                  {product.priceLabel}
                </div>
                <div
                  className="font-heading uppercase tracking-[0.1em] text-lg text-black/45 line-through leading-none pb-0.5"
                  data-testid="pdp-mrp"
                >
                  MRP {product.mrpLabel}
                </div>
                <div className="mp-save-badge" data-testid="pdp-save-badge">
                  Save {product.discountPercent}%
                </div>
              </div>
              <div className="mt-2 font-heading uppercase tracking-[0.16em] text-[11px] text-black/50 font-semibold">
                {product.priceNote}
              </div>
            </SectionReveal>

            <SectionReveal delay={200}>
              <div className="mt-6 pt-6 border-t border-black/10">
                <div
                  className="font-heading uppercase tracking-[0.16em] text-sm font-semibold"
                  style={{ color: "var(--mp-green-hover)" }}
                >
                  {product.kicker}
                </div>
                <h2 className="mt-2 font-display text-2xl sm:text-3xl uppercase leading-[0.95] text-black">
                  {product.headline}
                </h2>
                <p className="mt-3 font-body text-black/75 leading-relaxed">{product.subhead}</p>
              </div>
            </SectionReveal>

            <SectionReveal delay={260}>
              <p
                className="mt-6 font-body text-black/75 leading-relaxed"
                data-testid="pdp-short-description"
              >
                {product.shortDescription}
              </p>
            </SectionReveal>

            <SectionReveal delay={320}>
              <div className="mt-8">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-heading uppercase tracking-[0.18em] text-[11px] text-black/60 font-semibold">
                    Flavour
                  </span>
                  <span className="font-heading uppercase tracking-[0.16em] text-[12px] text-black font-semibold">
                    {activeFlavour.name}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2" data-testid="pdp-flavour-selector">
                  {product.flavours.map((f) => {
                    const selected = f.id === flavour;
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => setFlavour(f.id)}
                        data-testid={`pdp-flavour-${f.id}`}
                        aria-pressed={selected}
                        className={`px-3 py-3 border font-heading uppercase tracking-[0.08em] text-[11px] font-semibold text-left transition-colors ${
                          selected
                            ? "border-black bg-black text-white"
                            : "border-black/20 text-black/80 hover:border-black"
                        }`}
                      >
                        {f.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </SectionReveal>

            <SectionReveal delay={360}>
              <div className="mt-6">
                <div className="mb-3 font-heading uppercase tracking-[0.18em] text-[11px] text-black/60 font-semibold">
                  Quantity
                </div>
                <div className="inline-flex items-center border border-black/20" data-testid="pdp-quantity">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    data-testid="pdp-quantity-decrease"
                    aria-label="Decrease quantity"
                    className="w-10 h-11 flex items-center justify-center text-black hover:bg-black hover:text-white transition-colors font-heading text-lg"
                  >
                    −
                  </button>
                  <span
                    className="w-12 text-center font-heading font-semibold text-black text-lg"
                    data-testid="pdp-quantity-value"
                  >
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => q + 1)}
                    data-testid="pdp-quantity-increase"
                    aria-label="Increase quantity"
                    className="w-10 h-11 flex items-center justify-center text-black hover:bg-black hover:text-white transition-colors font-heading text-lg"
                  >
                    +
                  </button>
                </div>
              </div>
            </SectionReveal>

            <SectionReveal delay={420}>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <a
                  href={product.shopUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pdp-shop-now-button"
                  className="mp-btn mp-btn-primary w-full"
                  style={{ color: "rgb(20, 20, 20)" }}
                >
                  Shop Now
                </a>
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  data-testid="pdp-buy-button"
                  className="mp-btn-amazon w-full"
                >
                  Coming Soon On Amazon
                </button>
              </div>
              <div className="mt-5 flex items-center gap-3 text-black/75 text-sm font-body">
                <ShieldCheck size={16} style={{ color: "var(--mp-green-hover)" }} />
                <span>
                  100% Genuine · Verifiable on the{" "}
                  <a
                    href="https://musclepronutrition.com/authenticate/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pdp-truempn-link"
                    className="text-black hover:text-[color:var(--mp-green-hover)] underline underline-offset-4 font-semibold"
                  >
                    TrueMPN app
                  </a>
                </span>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* Intro strip */}
      <section
        style={{ background: "var(--mp-green)" }}
        className="text-black py-8 border-y border-black/10"
      >
        <div className="mp-container">
          <p
            className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase leading-none tracking-tight"
            data-testid="pdp-intro-line"
          >
            {product.intro}
          </p>
        </div>
      </section>

      {/* KEY BENEFITS */}
      <section data-testid="pdp-benefits" className="bg-[color:var(--mp-off-white)] py-20 lg:py-24">
        <div className="mp-container">
          <SectionReveal>
            <div className="text-center mb-12 max-w-3xl mx-auto">
              <div className="mp-eyebrow-green">Key Benefits</div>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
                What makes it work.
              </h2>
            </div>
          </SectionReveal>

          <SectionReveal>
            <PlaceholderImage
              label={`Product Benefit Banner · ${product.name}`}
              ratio="banner"
              testId="pdp-benefit-banner"
              src={BENEFIT_BANNERS[product.slug]}
              alt={`${product.name} — ${product.headline}`}
            />
          </SectionReveal>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {product.benefits.map((b, i) => (
              <SectionReveal key={b.label} delay={i * 80}>
                <div
                  data-testid={`pdp-benefit-${i}`}
                  className="h-full bg-white border border-black/10 p-6"
                >
                  <div
                    className="font-display text-4xl leading-none"
                    style={{ color: "var(--mp-green-hover)" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-4 font-heading uppercase tracking-[0.14em] text-sm text-black font-semibold">
                    {b.label}
                  </div>
                  <p className="mt-2 font-body text-black/70 text-sm leading-relaxed">{b.note}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* INGREDIENTS + SUGGESTED USE */}
      <section data-testid="pdp-ingredients-suggested" className="bg-white py-20 lg:py-24">
        <div className="mp-container">
          <SectionReveal>
            <div className="mb-10 max-w-3xl">
              <div className="mp-eyebrow-green">Nutrition & Use</div>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl uppercase leading-[0.95]">
                Ingredients & Suggested Use
              </h2>
            </div>
          </SectionReveal>

          <SectionReveal>
            <PlaceholderImage
              label="Ingredient Section Graphic"
              ratio="banner"
              testId="pdp-ingredient-graphic"
              src={ingredientSrc}
              alt={`${product.name} — ingredients`}
            />
          </SectionReveal>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
            <SectionReveal>
              <div className="font-heading uppercase tracking-[0.24em] text-[11px] text-black/50 font-semibold">
                Ingredients
              </div>
              <h3 className="mt-2 font-display text-2xl sm:text-3xl uppercase leading-none">
                What&apos;s inside
              </h3>
              <div className="mt-5 border border-black/10 p-6 bg-[color:var(--mp-off-white)]">
                <p className="font-body text-black/75 leading-relaxed">
                  {product.ingredients}
                </p>
              </div>
            </SectionReveal>

            <SectionReveal delay={120}>
              <div className="font-heading uppercase tracking-[0.24em] text-[11px] text-black/50 font-semibold">
                Suggested Use
              </div>
              <h3 className="mt-2 font-display text-2xl sm:text-3xl uppercase leading-none">
                How to take it
              </h3>
              <div className="mt-5 border border-black/10 p-6 bg-[color:var(--mp-off-white)]">
                <p className="font-body text-black/75 leading-relaxed">
                  {product.suggestedUse}
                </p>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="pdp-faq" className="bg-[color:var(--mp-off-white)] py-20 lg:py-24">
        <div className="mp-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <SectionReveal className="lg:col-span-4">
            <div className="mp-eyebrow-green">FAQ</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl uppercase leading-[0.95]">
              Questions, answered.
            </h2>
            <p className="mt-5 font-body text-black/70 leading-relaxed">
              For anything not covered here, reach out via the{" "}
              <Link
                to="/contact"
                className="text-black hover:text-[color:var(--mp-green-hover)] underline underline-offset-4 font-semibold"
              >
                Contact page
              </Link>
              .
            </p>
          </SectionReveal>

          <div className="lg:col-span-8">
            {product.faqs.map((f, i) => (
              <AccordionRow key={i} id={i} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section data-testid="pdp-related" className="bg-white py-20 lg:py-24">
        <div className="mp-container">
          <SectionReveal>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
              <div>
                <div className="mp-eyebrow-green">Also in the Range</div>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl uppercase leading-[0.95]">
                  Related Products
                </h2>
              </div>
              <Link to="/products" data-testid="pdp-view-all" className="mp-btn mp-btn-outline">
                View All Products
              </Link>
            </div>
          </SectionReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {related.map((p, i) => (
              <SectionReveal key={p.slug} delay={i * 100}>
                <ProductCard product={p} testId={`pdp-related-${p.slug}`} />
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
